import { supabase, invokeFunction } from '@/lib/supabase';
import type { Tables } from '@repo/supabase-client';

export type ApprovedSender = Tables<'approved_senders'>;

export async function getApprovedSenders(
  householdId: string,
): Promise<ApprovedSender[]> {
  const { data, error } = await supabase
    .from('approved_senders')
    .select('*')
    .eq('household_id', householdId)
    .order('created_at');

  if (error) throw error;
  return data ?? [];
}

export async function addApprovedSender(
  householdId: string,
  type: string,
  value: string,
): Promise<void> {
  // Idempotent upsert against UNIQUE (household_id, type, value). A second
  // Continue tap in onboarding (or a double-click of Add in Settings) used
  // to surface as 23505 → "Couldn't save your senders" even though the row
  // already existed from the first try. `ignoreDuplicates` keeps any
  // existing row untouched — including a system_managed one auto-seeded
  // by gmail-forwarding-setup — so the manual add silently no-ops.
  const { error } = await supabase
    .from('approved_senders')
    .upsert(
      { household_id: householdId, type, value },
      { onConflict: 'household_id,type,value', ignoreDuplicates: true },
    );

  if (error) {
    // The database owns the rule about what may be approved (YAR-355), because
    // this write goes straight to PostgREST and a check in the client would be
    // advice rather than a control. Re-implementing the rule here to produce a
    // nicer message would put a second copy of it in the codebase, which is the
    // shape that already caused a bug once (SUPPORTED_GMAIL_DOMAINS drifted
    // between two files). So the client translates the constraint, it does not
    // duplicate the reasoning behind it.
    if (error.message?.includes('approved_senders_domain_approvable')) {
      throw new Error(
        `"${value}" can't be approved as a whole domain. Personal mail providers ` +
          'cover everyone who has an account there — add the specific email ' +
          'address instead.',
      );
    }
    throw error;
  }
}

export async function removeApprovedSender(id: string): Promise<void> {
  const { error } = await supabase
    .from('approved_senders')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function validateDomain(
  domain: string,
): Promise<{ valid: boolean; error?: string; transient?: boolean }> {
  const { data, error } = await invokeFunction('validate-domain', {
    body: { domain },
  });
  if (error) {
    console.warn('[validateDomain] invocation error:', error);
    return { valid: false, error: 'Validation failed' };
  }
  return data;
}
